import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { SecretInput } from "@/components/design/SecretInput";
import { Switch } from "@/components/design/Switch";
import { LocalizedError } from "@/lib/localized-error";
import { ModelConfigError } from "@/lib/model-config";
import { usePendingScope } from "@/lib/pending-edits";
import { loadResearchSources, saveResearchSources, type ResearchSourceSetting } from "@/lib/research-sources";
import { SettingsCard } from "./SettingsCard";
import { modelConfigSaveErrorMessage } from "./model-config-save-error";

// Research sources (Settings · KI): the specialist databases the assistant may search besides the web,
// one tile each in the provider-tile grid. The switch and key are stored in gemstone, where the model gateway
// reads them; the Studio's globe then lets a question search PubMed too, and the card says so. Its edits
// register with the app-wide SaveBar
// (@adr SPARK-0005-unified-deferred-save-bar@1) as their own scope.

const PUBMED_DEFAULT: ResearchSourceSetting = { enabled: false, apiKey: "" };
const PUBMED_KEY_PROPERTY = "MODEL_GATEWAY_SOURCES__PUBMED__API_KEY";
const NCBI_ACCOUNT_SETTINGS = "https://account.ncbi.nlm.nih.gov/settings/";

export function ResearchSourcesCard() {
  const intl = useIntl();
  const [pubmed, setPubmed] = useState<ResearchSourceSetting>(PUBMED_DEFAULT);
  const [stored, setStored] = useState<ResearchSourceSetting>(PUBMED_DEFAULT);

  // A failed load (not admin / gemstone down) leaves the defaults, like the provider tiles.
  useEffect(() => {
    let cancelled = false;
    loadResearchSources()
      .then((state) => {
        if (cancelled) return;
        const loaded = state.sources.pubmed ?? PUBMED_DEFAULT;
        setStored(loaded);
        // Identity check: an edit made before the load landed survives it, as an unsaved edit.
        setPubmed((current) => (current === PUBMED_DEFAULT ? loaded : current));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const persist = async () => {
    try {
      await saveResearchSources({ sources: { pubmed } });
    } catch (e) {
      const status = e instanceof ModelConfigError ? e.status : 0;
      throw new LocalizedError(modelConfigSaveErrorMessage(intl, status), { cause: e });
    }
    setStored(pubmed);
  };

  const dirty = pubmed.enabled !== stored.enabled || pubmed.apiKey !== stored.apiKey;
  usePendingScope("research-sources", { dirty, save: persist, discard: () => setPubmed(stored) });

  const connected = pubmed.apiKey.trim().length > 0;
  const offerLabel = intl.formatMessage({ id: "model.sources.pubmed.offer", defaultMessage: "Offer in the Studio" });

  return (
    <SettingsCard
      title={intl.formatMessage({ id: "model.sources.title", defaultMessage: "Research sources" })}
      sub={intl.formatMessage({
        id: "model.sources.sub",
        defaultMessage: "Specialist databases the assistant may search besides the web.",
      })}
    >
      <div className="grid grid-cols-1 items-start gap-3 @sm:grid-cols-3">
        <div className="box p-4">
          <div className="flex items-center justify-between gap-2">
            <span className="text-smycles-body font-medium">PubMed</span>
            {connected ? <span className="h-[7px] w-[7px] rounded-full bg-accent pulse-dot" aria-hidden /> : null}
          </div>
          <div className="smycles-field-meta mt-1 text-ink-muted">
            {intl.formatMessage({
              id: "model.sources.pubmed.publisher",
              defaultMessage: "NCBI · U.S. National Library of Medicine",
            })}
          </div>

          <div className="mt-4 flex items-center gap-2">
            <Switch
              id="research-source-pubmed-enabled"
              checked={pubmed.enabled}
              onChange={(enabled) => setPubmed((p) => ({ ...p, enabled }))}
              ariaLabel={offerLabel}
            />
            <span className="smycles-field-value">{offerLabel}</span>
          </div>

          <label htmlFor="research-source-pubmed-key" className="smycles-field-label mb-2 mt-4 block">
            {intl.formatMessage({ id: "model.sources.keyLabel", defaultMessage: "API key" })}
          </label>
          <SecretInput
            id="research-source-pubmed-key"
            value={pubmed.apiKey}
            onChange={(apiKey) => setPubmed((p) => ({ ...p, apiKey }))}
            ariaLabel={intl.formatMessage(
              { id: "model.keyAriaLabel", defaultMessage: "{provider} API key" },
              { provider: "PubMed" },
            )}
            placeholder={intl.formatMessage({ id: "model.sources.pubmed.keyPlaceholder", defaultMessage: "your NCBI API key" })}
          />
          <div className="smycles-field-meta mt-1 break-all font-mono text-ink-muted">{PUBMED_KEY_PROPERTY}</div>

          <p className="smycles-field-meta mt-4 text-ink-muted">
            {intl.formatMessage({
              id: "model.sources.pubmed.hint",
              defaultMessage:
                "Articles and abstracts from PubMed through the NCBI E-utilities. Up to 3 requests per second without a key, 10 with one.",
            })}{" "}
            <a
              href={NCBI_ACCOUNT_SETTINGS}
              target="_blank"
              rel="noreferrer"
              className="underline decoration-ink/30 underline-offset-2 hover:text-ink-accent"
            >
              {intl.formatMessage({
                id: "model.sources.pubmed.keyLink",
                defaultMessage: "Create a key in your NCBI account",
              })}
            </a>
          </p>
        </div>
      </div>

      <div className="box mt-4 bg-ink/[0.02] p-3 smycles-field-meta text-ink">
        {intl.formatMessage({
          id: "model.sources.usage",
          defaultMessage:
            "In the Studio the assistant also searches these sources when the globe is on. The search term then goes to the source's provider.",
        })}
      </div>
    </SettingsCard>
  );
}
